'use client';

import Loading from '@/app/(withLayout)/loading';
import { getStepInitRequest, initializedStep } from '@/lib/stepInit';
import { setAdditionalData } from '@/redux/additionalDataSlice';
import { appendVariables, pushAccessKey, pushDummyStep, pushFlowData, pushInitializeStepData } from '@/redux/flowSlice';
import { pushErrorReason, pushPreinitializedStep, pushStepName } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { additionalDataType, initializeStep, instanceResponse, step, stepNames, steps } from '@repo/onb-api';
import { deleteClientCookie, failureDetails, setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { sendOnbEvent } from './newRelic';
import { StepDataContext } from './stepData';

/**
 * Loads an instance fetched by the server into Redux, as the flow expects it on the client. It only dispatches, so it
 * can run while rendering (server render and hydration); the cookies are stored by {@link persistInstanceCookies}.
 *
 * @param {Dispatch} dispatch - Redux dispatch.
 * @param {instanceResponse} initialData - The instance.
 * @param {string} [accessKey] - The accessKey that opened it, if any.
 * @param {string} [utmData] - Campaign params from the URL, as a JSON string.
 */
const loadInstance = (
  dispatch: ReturnType<typeof useDispatch>,
  initialData: instanceResponse,
  accessKey?: string,
  utmData?: string,
) => {
  if (accessKey) dispatch(pushAccessKey(accessKey));
  dispatch(pushFlowData(initialData));
  const additionalData = JSON.parse(initialData.variables?.additionalData || '{}') as additionalDataType;
  const utmValue = utmData && JSON.parse(utmData)?.length ? utmData : additionalData.utmData;
  dispatch(setAdditionalData({ ...additionalData, utmData: utmValue }));
};

/**
 * Stores the cookies of an instance loaded with {@link loadInstance}.
 *
 * @param {instanceResponse} initialData - The instance.
 * @param {string} [accessKey] - The accessKey that opened it, if any.
 */
const persistInstanceCookies = (initialData: instanceResponse, accessKey?: string) => {
  if (accessKey && initialData?.id) {
    // if another instace id was present, replace with the new one
    deleteClientCookie(['orderToken', 'utmData', 'accessKey']);
    setClientCookie('instanceId', initialData.id);
  }
  if (accessKey) setClientCookie('accessKey', accessKey);
};

/**
 * Puts a step initialized ahead of time (by the server page, or by the submit that led to it) in Redux and marks it,
 * so CheckClientData renders it right away instead of initializing it again from the browser.
 *
 * @param {Dispatch} dispatch - Redux dispatch.
 * @param {initializedStep} step - The initialized step.
 */
export const pushInitializedStep = (dispatch: ReturnType<typeof useDispatch>, step: initializedStep) => {
  if (step.appendedVariables) dispatch(appendVariables({ variables: step.appendedVariables }));
  dispatch(pushInitializeStepData({ stepName: step.stepName, variables: step.variables }));
  dispatch(pushPreinitializedStep(step.stepName));
};

const noopSubscribe = () => () => {};
/** True on the server and while React hydrates, false when mounting on a client navigation */
const useIsHydrating = () =>
  useSyncExternalStore(
    noopSubscribe,
    () => false,
    () => true,
  );

/**
 * Initializes the client flow by setting the dummy step in Redux and deleting the instanceId cookie.
 *
 * @param {stepNames} stepName - The name of the step to set as dummy.
 * @param {React.JSX.Element} children - The JSX element to render.
 * @param {string} utmData - The searchParams utmData string to set as cookie.
 * @return {React.JSX.Element} The JSX element representing the loading state.
 */
export function InitializeDummyFlow({
  stepName,
  children,
  utmData,
}: {
  stepName: stepNames;
  children: React.JSX.Element;
  utmData?: string;
}): React.JSX.Element {
  const dispatch = useDispatch();
  deleteClientCookie(['instanceId', 'orderToken', 'requestedAmount', 'utmData', 'accessKey']);
  useEffect(() => {
    dispatch(setAdditionalData({ requestedAmount: '' }));
    // deleteClientCookie(['requestedAmount']);
    const utmValue = utmData && JSON.parse(utmData)?.length ? utmData : '';
    dispatch(setAdditionalData({ utmData: utmValue }));
    dispatch(pushStepName(stepName));
    dispatch(pushDummyStep(stepName));
  }, [dispatch]);
  return children;
}

/**
 * Checks if the client data is correct and renders the provided children if it is.
 * If the client data is not correct, it redirects to the flow page.
 *
 * A step that arrives already initialized (the server page did it on a full page load, or the submit that led here
 * did it in the same server action) renders right away, also in the server HTML, without initializing it again.
 *
 * @param {stepNames} stepName - The name of the step to check.
 * @param {instanceResponse} [initialData] - Instance fetched by the server, loaded into Redux before rendering.
 * @param {initializedStep} [initialStep] - The step, already initialized by the server.
 * @param {string} [replaceUrl] - URL to show once mounted, when the server rendered a step at another URL (/flow).
 * @param {React.JSX.Element} children - The JSX element to render if the client data is correct.
 * @return {React.JSX.Element} The JSX element to render.
 */
export function CheckClientData({
  instanceId,
  stepName,
  initialData,
  initialStep,
  replaceUrl,
  accessKey: initialAccessKey,
  utmData,
  children,
}: {
  jwt?: string;
  instanceId?: string;
  stepName: stepNames;
  /** Instance fetched by the server: loaded here instead of bouncing through /flow */
  initialData?: instanceResponse;
  initialStep?: initializedStep;
  replaceUrl?: string;
  accessKey?: string;
  utmData?: string;
  children: React.JSX.Element;
}): React.JSX.Element {
  // const date = new Date();
  // date.setTime(date.getTime() + 3 * 60 * 60 * 1000); // Adding 3 hours in milliseconds
  // const cookieTtl = date.toUTCString();

  // This cookie is set only in a 'onb-cuotas' instance type.
  if (instanceId) {
    // setClientCookie('instanceId', instanceId, cookieTtl);
    setClientCookie('instanceId', instanceId);
  }

  const router = useRouter();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const isHydrating = useIsHydrating();
  const { variables, steps, id, accessKey } = useSelector((state: RootState) => state.flowdata);
  const additionalData = useSelector((state: RootState) => state.additionalData);

  /**
   * Loads the server's instance (and its initialized step) into Redux, once, and takes the step if it arrived
   * initialized. Returns that step, or undefined when it still has to be initialized here.
   */
  const prepared = useRef<{ done: boolean; step?: step }>({ done: false });
  const prepare = () => {
    if (prepared.current.done) return prepared.current.step;
    prepared.current.done = true;
    if (initialData) {
      loadInstance(dispatch, initialData, initialAccessKey, utmData);
      if (initialStep?.stepName === stepName) pushInitializedStep(dispatch, initialStep);
    }
    const { flowdata, navigationdata } = store.getState();
    const preinitialized = flowdata.steps.find((s) => s.name === stepName);
    if (navigationdata.preinitializedStep === stepName && preinitialized?.initialized) {
      dispatch(pushPreinitializedStep(undefined));
      dispatch(pushStepName(stepName));
      prepared.current.step = preinitialized;
    }
    return prepared.current.step;
  };
  // Server render and hydration: Redux has to hold the step before its first render, so the server HTML already shows
  // it and hydration matches. Nothing else is mounted on the client yet that could re-render mid-render
  if (isHydrating) prepare();

  const [currentStep, setCurrentStep] = useState<step | undefined>(() => prepared.current.step);
  const [stepData, setStepData] = useState<object | undefined>(() =>
    prepared.current.step ? prepared.current.step.variables || {} : undefined,
  );
  const [isInitialized, setIsInitialized] = useState(() => !!prepared.current.step);
  const [areVariablesAppended, setAreVariablesAppended] = useState(() => !!prepared.current.step);

  // Client navigation: same, right after mounting and before the browser paints, so the skeleton never shows
  useLayoutEffect(() => {
    if (prepared.current.done) return;
    const preinitialized = prepare();
    if (preinitialized) {
      setIsInitialized(true);
      setAreVariablesAppended(true);
      setCurrentStep(preinitialized);
      setStepData(preinitialized.variables || {});
    }
  }, []);
  useEffect(() => {
    if (initialData) persistInstanceCookies(initialData, initialAccessKey);
    // The server rendered the step at /flow: show its own URL, without another navigation. Deferred: the router hooks
    // into history.replaceState (to keep usePathname in sync) in an effect that runs after this one on hydration
    if (!replaceUrl) return;
    const timeout = setTimeout(() => {
      if (window.location.pathname !== replaceUrl) window.history.replaceState(null, '', replaceUrl);
    });
    return () => clearTimeout(timeout);
  }, []);
  useEffect(() => {
    // From the store: the server's instance may have been loaded after this render's selector ran
    const _currentStep = store.getState().flowdata.steps.find((s) => s.name === stepName);
    if (!_currentStep) {
      // At this point we are missing the step data, redirect to the flow page
      router.replace('/flow');
      router.refresh();
    } else {
      setCurrentStep(_currentStep);
    }
  }, [steps, stepName]);
  useEffect(() => {
    if (currentStep?.initialized) {
      if (currentStep.isEndOfFlow) {
        deleteClientCookie([
          'instanceId',
          'orderToken',
          'integrationType',
          'policyProduct',
          'requestedAmount',
          'utmData',
          'accessKey',
        ]);
      }
      setStepData(currentStep?.variables || {});
    }
  }, [currentStep]);
  useEffect(() => {
    if (!isInitialized && id && stepName === currentStep?.name && areVariablesAppended) {
      let error = false;
      let failure: Record<string, string | undefined> = {};
      setIsInitialized(true);
      initializeStep<steps[typeof stepName]>({
        instanceId: id,
        name: stepName,
        variables: getStepInitRequest(stepName, variables, additionalData).initVariables,
        accessKey: accessKey,
      })
        .then((response) => {
          if (response.hardError || response.softError) {
            error = true;
            failure = { ...failureDetails(response), error: response.error, attribute: response.attribute };
            if (response.error === 'REQUIRED_ATTRIBUTE' && response.attribute === 'accessKey') {
              dispatch(pushErrorReason('accessKey'));
            }
          } else {
            dispatch(pushInitializeStepData({ stepName, variables: response.variables }));
            dispatch(pushStepName(stepName));
          }
        })
        .catch((e) => {
          console.error('Initialize Step Error');
          error = true;
          failure = failureDetails(undefined, e);
        })
        .finally(() => {
          if (error) {
            sendOnbEvent({ type: 'error', subtype: 'initStep', data: { stepName, instanceId: id, ...failure } });
            router.push('/error');
          }
        });
    }
    //Will try to append local variables before step initialization
    if (!isInitialized && id && stepName === currentStep?.name && !areVariablesAppended) {
      const { appendedVariables } = getStepInitRequest(stepName, variables, additionalData);
      if (appendedVariables) {
        dispatch(appendVariables({ variables: appendedVariables }));
      }
      setAreVariablesAppended(true);
    }
  }, [isInitialized, id, stepName, dispatch, variables, currentStep, areVariablesAppended]);

  // children come from the server page as an opaque node, so step data goes through context instead of props
  return stepData ? <StepDataContext value={stepData}>{children}</StepDataContext> : <Loading />;
}

/**
 * Sends one endUtmFlow per campaign param when the flow reaches SUCCESS, so finished sales can be attributed to
 * campaigns. Reads the params the flow keeps in Redux (from the landing URL, or the instance additionalData on resume).
 *
 * @param {string} [instanceId] - The finished instance.
 * @param {string} [integrationType] - Integration of the instance, if any.
 * @param {string} [policyProduct] - Product of the instance, if any.
 * @return {null} Renders nothing.
 */
export function EndUtmFlow({
  instanceId,
  integrationType,
  policyProduct,
}: {
  instanceId?: string;
  integrationType?: string;
  policyProduct?: string;
}): null {
  const { utmData, requestedAmount } = useSelector((state: RootState) => state.additionalData);
  useEffect(() => {
    let params: { utmParam?: string; utmValue?: string }[];
    try {
      params = utmData ? JSON.parse(utmData) : [];
    } catch {
      return;
    }
    if (!Array.isArray(params)) return;
    params.forEach((param) => {
      sendOnbEvent({
        type: 'utmEvent',
        subtype: 'endUtmFlow',
        integrationType,
        policyProduct,
        data: { ...param, requestedAmount, instanceId },
      });
    });
    // Once per SUCCESS render
  }, []);
  return null;
}
